import React, { useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { format, parseISO, eachDayOfInterval, subDays } from 'date-fns';
import { useThemeStore } from '../../stores';
import type { TransactionTrendData } from '../../types';

interface TransactionChartProps {
  data: TransactionTrendData;
  days?: number;
}

interface ChartDataPoint {
  date: string;
  label: string;
  transactions: number;
}

const COLORS = {
  light: {
    line: '#0891b2',
    fill: 'rgba(8, 145, 178, 0.1)',
    grid: '#e2e8f0',
    axis: '#64748b',
    tooltipBg: '#ffffff',
    tooltipBorder: '#e2e8f0',
    tooltipText: '#0f172a',
  },
  dark: {
    line: '#22d3ee',
    fill: 'rgba(34, 211, 238, 0.1)',
    grid: '#334155',
    axis: '#94a3b8',
    tooltipBg: '#1e293b',
    tooltipBorder: '#334155',
    tooltipText: '#f1f5f9',
  },
};

const CustomTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
  isDark: boolean;
}> = ({ active, payload, label, isDark }) => {
  if (!active || !payload?.length) return null;

  const colors = isDark ? COLORS.dark : COLORS.light;

  return (
    <div
      className="rounded-lg px-4 py-3 shadow-elevated border"
      style={{
        backgroundColor: colors.tooltipBg,
        borderColor: colors.tooltipBorder,
      }}
    >
      <p
        className="text-sm font-medium mb-2"
        style={{ color: colors.tooltipText }}
      >
        {label}
      </p>
      <div className="flex items-center gap-2 text-sm">
        <span
          className="w-2.5 h-2.5 rounded-full"
          style={{ backgroundColor: colors.line }}
        />
        <span style={{ color: colors.axis }}>
          Transactions:
        </span>
        <span className="font-semibold" style={{ color: colors.tooltipText }}>
          {payload[0].value}
        </span>
      </div>
    </div>
  );
};

export const TransactionChart: React.FC<TransactionChartProps> = ({ data, days = 15 }) => {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const colors = isDark ? COLORS.dark : COLORS.light;

  const chartData = useMemo((): ChartDataPoint[] => {
    const today = new Date();
    const startDate = subDays(today, days - 1);
    const allDays = eachDayOfInterval({ start: startDate, end: today });

    const txMap = new Map<string, number>();
    data.transactions.forEach((d) => {
      const key = format(parseISO(d.date), 'yyyy-MM-dd');
      txMap.set(key, d.count);
    });

    return allDays.map((day) => {
      const key = format(day, 'yyyy-MM-dd');
      return {
        date: key,
        label: format(day, 'MMM d'),
        transactions: txMap.get(key) || 0,
      };
    });
  }, [data, days]);

  return (
    <div className="w-full h-[320px]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={chartData}
          margin={{ top: 8, right: 8, left: -12, bottom: 0 }}
        >
          <defs>
            <linearGradient id="txGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={colors.line} stopOpacity={0.2} />
              <stop offset="100%" stopColor={colors.line} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={colors.grid}
            vertical={false}
          />
          <XAxis
            dataKey="label"
            tick={{ fill: colors.axis, fontSize: 12 }}
            tickLine={false}
            axisLine={{ stroke: colors.grid }}
            interval="preserveStartEnd"
            tickMargin={8}
          />
          <YAxis
            tick={{ fill: colors.axis, fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
            tickMargin={4}
          />
          <Tooltip
            content={<CustomTooltip isDark={isDark} />}
            cursor={{ stroke: colors.grid, strokeDasharray: '4 4' }}
          />
          <Area
            type="monotone"
            dataKey="transactions"
            stroke={colors.line}
            strokeWidth={2.5}
            fill="url(#txGradient)"
            activeDot={{
              r: 5,
              fill: colors.line,
              stroke: isDark ? '#1e293b' : '#ffffff',
              strokeWidth: 2,
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TransactionChart;
